/**
 * Outing Context - Global state for outing management
 * Modular and extractable for standalone app use.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const OutingContext = createContext(null);

export function OutingProvider({ children, apiBaseUrl }) {
  const [currentOuting, setCurrentOuting] = useState(null);
  const [teams, setTeams] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = apiBaseUrl || process.env.REACT_APP_BACKEND_URL;

  // Fetch outing details
  const fetchOuting = useCallback(async (outingId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/outings/${outingId}`);
      const data = await response.json();
      if (data.success) {
        setCurrentOuting(data.outing);
      } else {
        setError(data.detail || 'Failed to fetch outing');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Fetch teams
  const fetchTeams = useCallback(async (outingId) => {
    try {
      const response = await fetch(`${API_BASE}/api/outings/${outingId}/teams`);
      const data = await response.json();
      if (data.success) {
        setTeams(data.teams);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [API_BASE]);

  // Fetch invitations
  const fetchInvitations = useCallback(async (outingId) => {
    try {
      const response = await fetch(`${API_BASE}/api/outings/${outingId}/invitations`);
      const data = await response.json();
      if (data.success) {
        setInvitations(data.invitations);
      }
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    }
  }, [API_BASE]);

  // Fetch payments
  const fetchPayments = useCallback(async (outingId) => {
    try {
      const response = await fetch(`${API_BASE}/api/outings/${outingId}/payments`);
      const data = await response.json();
      if (data.success) {
        setPayments(data.payments);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    }
  }, [API_BASE]);

  // Fetch scorecards
  const fetchScorecards = useCallback(async (outingId) => {
    try {
      const response = await fetch(`${API_BASE}/api/outings/${outingId}/scorecards`);
      const data = await response.json();
      if (data.success) {
        setScorecards(data.scorecards);
      }
    } catch (err) {
      console.error('Failed to fetch scorecards:', err);
    }
  }, [API_BASE]);

  // Fetch leaderboard
  const fetchLeaderboard = useCallback(async (outingId) => {
    try {
      const response = await fetch(`${API_BASE}/api/outings/${outingId}/leaderboard`);
      const data = await response.json();
      if (data.success) {
        setLeaderboard(data);
      }
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  }, [API_BASE]);

  // Fetch notifications
  const fetchNotifications = useCallback(async (outingId, since = null) => {
    try {
      let url = `${API_BASE}/api/outings/${outingId}/notifications`;
      if (since) {
        url += `?since=${encodeURIComponent(since)}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        if (since) {
          // Append new notifications
          setNotifications(prev => [...data.notifications, ...prev].slice(0, 50));
        } else {
          setNotifications(data.notifications);
        }
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [API_BASE]);

  // Refresh all data
  const refreshAll = useCallback(async (outingId) => {
    await Promise.all([
      fetchOuting(outingId),
      fetchTeams(outingId),
      fetchInvitations(outingId),
      fetchPayments(outingId),
      fetchScorecards(outingId),
      fetchLeaderboard(outingId),
      fetchNotifications(outingId)
    ]);
  }, [fetchOuting, fetchTeams, fetchInvitations, fetchPayments, fetchScorecards, fetchLeaderboard, fetchNotifications]);

  // Add notification locally (optimistic update)
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev].slice(0, 50));
  }, []);

  const value = {
    // State
    currentOuting,
    teams,
    invitations,
    payments,
    scorecards,
    leaderboard,
    notifications,
    loading,
    error,
    
    // Actions
    setCurrentOuting,
    setTeams,
    setInvitations,
    setPayments,
    setScorecards,
    setLeaderboard,
    setNotifications,
    
    // Fetchers
    fetchOuting,
    fetchTeams,
    fetchInvitations,
    fetchPayments,
    fetchScorecards,
    fetchLeaderboard,
    fetchNotifications,
    refreshAll,
    addNotification,
    
    // Config
    API_BASE
  };

  return (
    <OutingContext.Provider value={value}>
      {children}
    </OutingContext.Provider>
  );
}

export function useOuting() {
  const context = useContext(OutingContext);
  if (!context) {
    throw new Error('useOuting must be used within an OutingProvider');
  }
  return context;
}
